import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Buton, Iskelet, Kart, MetinAlani, Secim, Sekmeler } from "@koclukpanel/ui";
import { izinKapsami } from "./izin";
import { modulAdi } from "./modul-adi";
import {
  ApiHatasi,
  benGetir,
  etkinlikDokumu,
  etkinlikOzeti,
  personelListesi,
  type Ben,
  type EtkinlikOzeti,
  type KisiDokumu,
  type OzetSatiri,
} from "./api";

/**
 * TOPLU ETKINLIK OZETI — F-LOG-11, G-IZL-03a
 *
 * ================================================================
 * BU EKRAN "AKTIF SURE" GOSTERMIYOR
 * ================================================================
 * F-LOG-11'in olcmeye bagladigi sey personelin panelde gecirdigi
 * SURE ve o olcum, personel aydinlatma metni imzalanana kadar
 * baslatilmiyor. Burada gosterilen sey zaten yazilmis denetim
 * kayitlarinin SAYISI. Ekran bunu YAZIYOR — cunku "etkinlik raporu"
 * basligini goren biri dogal olarak sure sanar ve olculmeyen bir seyi
 * olculmus zannetmek, olcmekten daha kotu bir yanlis.
 *
 * ================================================================
 * GIZLENEN DEMET GORUNUYOR
 * ================================================================
 * Esigi dolduramayan demet listeden cikarilmiyor, "gizli" olarak
 * duruyor. Cikarsaydik okuyan "finansta hic islem yok" diye anlardi
 * — gizlemek ile yanlis bilgi vermek ayri seyler.
 */

const ARALIKLAR = [
  { gun: 7, etiket: "7 gün" },
  { gun: 30, etiket: "30 gün" },
  { gun: 90, etiket: "90 gün" },
] as const;

function enBuyuk(satirlar: readonly OzetSatiri[]): number {
  return satirlar.reduce((m, s) => (s.yayimlandi ? Math.max(m, s.islemSayisi) : m), 0);
}

function Demetler({
  baslik,
  satirlar,
  aciklama,
}: {
  baslik: string;
  satirlar: readonly OzetSatiri[];
  aciklama: string;
}) {
  const tavan = enBuyuk(satirlar);

  return (
    <Kart>
      <h3 className="text-notr-800 text-sm font-semibold">{baslik}</h3>
      <p className="text-notr-600 mt-0.5 text-xs">{aciklama}</p>

      {satirlar.length === 0 ? (
        <p className="text-notr-600 mt-3 text-sm">Bu aralıkta kayıt yok.</p>
      ) : (
        <ul className="mt-3 flex flex-col gap-1.5">
          {satirlar.map((s) => (
            <li key={s.anahtar} className="flex items-center gap-2 text-sm">
              <span className="text-notr-700 w-40 shrink-0 truncate" title={s.anahtar}>
                {s.anahtar}
              </span>
              <span className="bg-notr-100 h-2 min-w-[2px] flex-1 overflow-hidden rounded-full">
                {s.yayimlandi && tavan > 0 && (
                  <span
                    className="bg-marka-500 block h-full rounded-full"
                    style={{ width: `${Math.max(2, (s.islemSayisi / tavan) * 100)}%` }}
                  />
                )}
              </span>
              {s.yayimlandi ? (
                <span className="text-notr-600 w-32 shrink-0 text-right">
                  {s.islemSayisi.toLocaleString("tr-TR")}
                  <span className="text-notr-600"> · {s.kisiSayisi} kişi</span>
                </span>
              ) : (
                /*
                 * "Gizli" diyor ama SEBEBI de yaziyor. Yalnizca "gizli"
                 * yazan bir satir, yetki eksikligi sanilirdi — oysa
                 * sebep isimsizligi korumak ve bu, izin verilse bile
                 * degismezdi.
                 */
                <span className="text-notr-600 w-32 shrink-0 text-right text-xs">
                  az kişi — gizli
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </Kart>
  );
}

function Ozet({ veri }: { veri: EtkinlikOzeti }) {
  const gizliSayisi = veri.moduller.filter((m) => !m.yayimlandi).length;
  const hepsiGizli = veri.moduller.length > 0 && gizliSayisi === veri.moduller.length;

  return (
    <div className="flex flex-col gap-4">
      {/*
       * TAMAMEN GIZLI RAPOR ACIKCA SOYLENIYOR.
       *
       * `ALL` kapsaminda kucuk demetler esigi dolduramayabiliyor ve
       * rapor bombos acilabiliyor. Bunu yazmayan bir ekran "hic islem
       * yok" izlenimi verirdi — oysa toplam dolu.
       *
       * Bu bant once TEAM kapsami icin yazilmisti; o kapsam
       * 07.09.2026'da katalogdan cikarildi (gerekcesi
       * `etkinlik-ozeti.ts` > `esik`). Bant KALIYOR: `ALL` kapsaminda
       * da dar bir aralikta ayni sey olabiliyor.
       */}
      {hepsiGizli && (
        <p className="border-uyari-200 bg-uyari-50 text-uyari-800 rounded-lg border p-3 text-sm">
          Bu aralıkta <strong>{veri.toplamIslem.toLocaleString("tr-TR")}</strong> işlem var ama
          hiçbir demet yayımlanamadı: görebildiğiniz kişi sayısı {veri.enAzKisi} kişilik isimsizlik
          eşiğinin altında. Rapor boş değil — <em>gösterilemiyor</em>.
        </p>
      )}

      <div className="border-notr-200 bg-notr-50 flex flex-wrap items-baseline gap-x-6 gap-y-1 rounded-lg border p-4">
        <span className="text-notr-900 text-2xl font-semibold">
          {veri.toplamIslem.toLocaleString("tr-TR")}
        </span>
        <span className="text-notr-600 text-sm">işlem</span>
        <span className="text-notr-600 text-xs">
          {new Date(veri.baslangic).toLocaleDateString("tr-TR")} –{" "}
          {new Date(veri.bitis).toLocaleDateString("tr-TR")}
        </span>
        <span className="text-notr-600 ml-auto text-xs">
          {kapsamMetni(veri.kapsam, veri.kendiRaporu)}
        </span>
      </div>

      <Demetler
        baslik="Modüllere göre"
        satirlar={veri.moduller}
        aciklama="Hangi modülde ne kadar işlem yapıldı"
      />
      <Demetler
        baslik="Olay kategorisine göre"
        satirlar={veri.kategoriler}
        aciklama="Kayıt türlerinin dağılımı"
      />
      <Demetler
        baslik="Günün saatlerine göre"
        satirlar={veri.saatler}
        aciklama="Yoğunluğun saatlere dağılımı (Europe/Istanbul)"
      />
    </div>
  );
}

/**
 * Kapsami kullanicinin anlayacagi bicimde yazar.
 *
 * "kapsam: all" bir ic terim; ekranda goren kisi icin hicbir sey
 * ifade etmiyor. Anlatilmak istenen sey RAPORUN KIMI KAPSADIGI.
 */
function kapsamMetni(kapsam: string, kendiRaporu: boolean): string {
  if (kendiRaporu) return "yalnızca kendi verileriniz";
  if (kapsam === "ALL") return "bütün kullanıcılar";
  /*
    TEAM DALI KALDIRILDI (07.09.2026) — kapsam katalogdan cikti.
    Tanimadigi bir kapsam icin ekran UYDURMUYOR: "gorebildiginiz
    kayitlar" hem dogru hem de ne oldugunu iddia etmiyor.
  */
  return "görebildiğiniz kayıtlar";
}

export function Etkinlik() {
  const [gun, setGun] = useState<number>(30);
  const ozet = useQuery({
    queryKey: ["etkinlik-ozeti", gun],
    queryFn: () => etkinlikOzeti(gun),
  });

  return (
    <div className="flex flex-col gap-5">
      <header className="flex flex-col gap-2">
        {/* Baslik KABUK tarafindan basiliyor; burada tekrar etmiyor. */}
        <div className="flex flex-wrap items-center gap-3">
          {/*
            ELLE YAZILAN ÇİP ŞERİDİ DEĞİL `Sekmeler`.

            Buradaki düğmeler `rounded-md px-2.5 py-1` ile çiziliyordu:
            **44px dokunma hedefi yoktu** ve basılı hâli `aria-pressed`
            taşımıyordu — yani telefonda zor basılıyor, ekran okuyucuda
            hangisinin seçili olduğu duyurulmuyordu.

            `Sekmeler` ikisini de veriyor ve panelin geri kalanıyla aynı
            görünüyor; şerit her ekranda başka türlü çizilirse kullanıcı
            aynı işi yapan iki şeyi ayrı sanır.
          */}
          <Sekmeler
            etiket="Zaman aralığı"
            secili={String(gun)}
            onSec={(d) => setGun(Number(d))}
            sekmeler={ARALIKLAR.map((a) => ({ deger: String(a.gun), etiket: a.etiket }))}
          />
        </div>

        {/*
         * Bu cumle sussleme degil: "etkinlik raporu" basligini goren
         * biri dogal olarak calisma suresi sanar. Olculmeyen bir seyi
         * olculmus zannetmek, olcmekten daha kotu bir yanlis.
         */}
        <p className="text-notr-600 text-sm">
          Bu rapor <strong>çalışma süresi değil</strong>: sistemde kaydı tutulan işlemlerin sayımı.
          Süre ölçümü, personel aydınlatma metni imzalanana kadar başlatılmıyor{/* F-LOG-11 */}.{" "}
          {/*
            ================================================================
            EŞİK SAYISI UYDURULMUYOR
            ================================================================
            Burada `?? 5` yazıyordu. Sunucudaki eşik SABİT DEĞİL, kapsama
            göre değişiyor: `esik()` `ALL` için 5, `SELF` için 0
            döndürüyor. Yani özet henüz gelmemişken ekran bir kullanıcıya
            yanlış sayıyı yazıyordu — hem de "neden gizli" sorusunun tam
            cevabı olarak.

            (Metin önce `TEAM` için 2 diyordu; o kapsam 07.09.2026'da
            katalogdan çıkarıldı. Sayının kaynağı değişmedi: hâlâ
            sunucudan geliyor, hâlâ burada varsayılanı tutulmuyor.)

            Bu, bu oturumda sudo penceresinde de düzelttiğimiz sınıfın
            aynısı: sunucudan gelen bir sayının istemcide varsayılanını
            tutmak, alanın hiç olmamasından daha sinsi bir yanlış — çünkü
            yanlış olduğunda da inandırıcı görünüyor.
          */}
          {ozet.data
            ? `Kimseyi işaret etmemesi için, arkasında ${ozet.data.enAzKisi} kişiden az olan demetler gizlenir.`
            : "Kimseyi işaret etmemesi için, arkasında yeterince kişi olmayan demetler gizlenir."}
        </p>
      </header>

      {ozet.isPending && <Iskelet satir={4} />}
      {/*
        ================================================================
        HER HATA "YETKİNİZ YOK" DEĞİL
        ================================================================
        Tek cümle bütün hataları karşılıyordu: sunucu 500 verse de, ağ
        kopsa da ekran kullanıcıya **yetkisi olmadığını** söylüyordu.
        İddia doğru görünüyordu çünkü en sık sebep buydu; ama gerekçe
        yanlıştı ve yanlış gerekçe yanlış davranış üretir — yetkisi
        olduğunu bilen kişi bunu okuyup yöneticiye gidiyor, oysa
        yapması gereken tek şey yeniden denemek.
      */}
      {ozet.isError && (
        <p className="text-notr-600 text-sm">
          {ozet.error instanceof ApiHatasi && ozet.error.durum === 403
            ? "Etkinlik özetini görme yetkiniz yok. Bu, kayıt tutulmadığı anlamına gelmez."
            : "Etkinlik özeti alınamadı. Bu, kayıt tutulmadığı anlamına gelmez — yeniden deneyin."}
        </p>
      )}
      {ozet.data && <Ozet veri={ozet.data} />}

      <KisiDokumuBolumu gun={gun} />
    </div>
  );
}

/** "Kendi dökümüm" seçeneğinin değeri — hiçbir kimlikle çakışmasın diye ayrı. */
const BEN = "__ben__";

/**
 * KİŞİ BAZLI ETKİNLİK DÖKÜMÜ — G-IZL-03a
 *
 * `GET /denetim/etkinlik/kisi` yazılıydı, hiçbir ekran çağırmıyordu.
 * İki ayrı işlevi birden kapsıyor:
 *
 * 1. KENDİ dökümün — gerekçesiz açılır. "Sistem benim hakkımda ne
 *    tutuyor" sorusunun cevabı panelde hiç yoktu. 14.09.2026'dan beri
 *    `denetim.etkinlik.kisi` yalnızca SUPER_ADMIN ve TECH_ADMIN'de
 *    (Emre'nin kararı, matris §5.17); diğerleri kendi verisini KVKK
 *    başvurusuyla ister. Bu yüzden "kısıtlanamaz" artık yazılmıyor.
 * 2. BAŞKASININ dökümü — `ALL` kapsamı + yazılı gerekçe ister ve
 *    açılışın KENDİSİ senkron bir güvenlik olayı olarak kaydedilir.
 *
 * ================================================================
 * BİRİNCİ İŞLEV BİR TUR BOYUNCA HİÇ ÇALIŞMADI
 * ================================================================
 * "Kendi dökümüm" seçeneği sunucuya `kullanici=ben` — kimlik değil,
 * DÜZ METİN — gönderiyordu. Sunucu `kullaniciId === c.userId`
 * karşılaştırması yapıyor; `"ben"` hiçbir kimliğe eşit olmadığı için
 * istek **başkasının dökümü** sayılıyor ve reddediliyordu. Üstelik
 * dönen cümle kullanıcıyı suçluyordu: *"Başkasının etkinlik dökümünü
 * açma yetkiniz yok. Kendi dökümünüzü görebilirsiniz."* — oysa
 * kullanıcı tam olarak kendi dökümünü istemişti.
 *
 * Kimin etkilendiği: `kisi.personel.goruntule` PDR Uzmanı, Satış ve
 * Finans rollerinde **hiç yok**. Onların açılır listesi boş kalıyor,
 * geriye yalnızca kırık seçenek kalıyordu — yani bu üç rol için KVKK
 * hakkı panelde **hiçbir yoldan** kullanılamıyordu.
 *
 * Sınıf tanıdık: **mekanizma var, izin var, cümle var — o cümleye
 * varan yol yok.**
 *
 * ================================================================
 * DÜZELTME — "ALL KİMSEDE YOK" YANLIŞTI (04.09.2026)
 * ================================================================
 * Burada önce şu yazıyordu: *"`denetim.etkinlik.kisi` dokuz rolün
 * hepsinde `SELF`, `ALL` kimsede yok."* **Yanlış.** `SUPER_ADMIN`de
 * `ALL`, ve o dal bugün canlı çalıştı (gerekçesiz 403, gerekçeli 200,
 * eş zamanlı `ACTIVITY_DETAIL_OPENED` güvenlik olayı).
 *
 * Neden yanlış ölçtüm: `seed/roles.ts` içinde `SUPER_ADMIN`in
 * `grants: []` — **boş**. İzinleri katalogdan üretiliyor
 * (`widestAllowed`, `defaultScope: "ALL"`), yani aranan satır dosyada
 * gerçekten **yok** ve olmaması gerekiyor. `grep` doğru çalıştı;
 * yanlış olan *"dosyada yoksa sistemde de yok"* varsayımıydı.
 * Doğruluk kaynağı katalog + üretim kuralı (ya da veritabanı) idi.
 *
 * Bu yüzden aşağıdaki `ALL` dalı **ölü kod değil**: `SUPER_ADMIN`
 * gerçekten başkasının dökümünü açabiliyor.
 *
 * ================================================================
 * GEREKÇE ALANI HER ZAMAN GÖRÜNMÜYOR
 * ================================================================
 * Kendi dökümünde gerekçe alanı hiç çizilmiyor. Çizseydik, kanunen
 * gerekçesiz olması gereken bir hak için kullanıcıya gerekçe yazdırmış
 * olurduk — ve çoğu kişi o alanı zorunlu sanıp vazgeçerdi.
 *
 * Alanın görünürlüğü artık `kim !== "ben"` ile değil KİMLİK
 * KARŞILAŞTIRMASIYLA belirleniyor: listeden kendi adını seçen kişi de
 * kendi dökümünü istiyor. Önceki hâlde o yol gerekçe dayatıyordu —
 * sunucu istemediği hâlde.
 */
function KisiDokumuBolumu({ gun }: { gun: number }) {
  const [kim, setKim] = useState<string>(BEN);
  const [gerekce, setGerekce] = useState("");
  const [istenen, setIstenen] = useState<{ kullanici: string; gerekce: string } | null>(null);

  /*
   * Aynı anahtar kabukta zaten kullanılıyor (`Uygulama.tsx`), yani
   * yanıt önbellekte: bu sorgu ek bir istek üretmiyor.
   */
  const ben = useQuery<Ben>({ queryKey: ["ben"], queryFn: benGetir, retry: false });
  const benimKimligim = ben.data?.userId ?? null;

  /*
   * Sunucunun kullandığı DEĞERİN AYNISI: `scopeOf(authz,
   * "denetim.etkinlik.kisi")`. Ekran kendi kuralını kurmuyor, aynı
   * haritayı okuyor — ve karar yine sunucuda veriliyor. Buradaki
   * kontrol yalnızca **her zaman reddedilecek bir yola düğme
   * çizmemek** için: `ALL` yoksa başkasının dökümü hiçbir gerekçeyle
   * açılmıyor, o hâlde ne kişi listesi ne gerekçe alanı gösterilmeli.
   */
  /*
    KAPSAM soruluyor, VARLIK degil: uc baskasinin dokumu icin `ALL`
    istiyor (`etkinlik.service.ts`). Dar kapsamli biri dugmeyi gorseydi
    her tiklamada 403 alirdi. Kod `PermissionCode` tipinden geciyor —
    yazim hatasi derlenmiyor.
  */
  const baskasiniGorebilir =
    ben.data !== undefined && izinKapsami(ben.data.izinler, "denetim.etkinlik.kisi") === "ALL";

  const personel = useQuery({
    queryKey: ["personel", false],
    queryFn: () => personelListesi(false),
    enabled: baskasiniGorebilir === true,
    retry: false,
  });

  const dokum = useQuery<KisiDokumu>({
    queryKey: ["etkinlik-dokumu", istenen?.kullanici, gun, istenen?.gerekce],
    queryFn: () => etkinlikDokumu(istenen!.kullanici, gun, istenen!.gerekce),
    enabled: istenen !== null,
    retry: false,
  });

  const secilenKimlik = kim === BEN ? benimKimligim : kim;
  const baskasi = secilenKimlik !== null && secilenKimlik !== benimKimligim;
  // Sunucudaki `DOKUM_GEREKCE_EN_AZ` ile aynı sayı. Sunucu yine de
  // kontrol ediyor; buradaki yalnızca boşuna gönderimi engelliyor.
  const gerekceKisa = baskasi && gerekce.trim().length < 15;
  const kimlikYok = secilenKimlik === null;

  /*
   * Kendi kaydımız listede İKİNCİ KEZ görünmüyor. Görünseydi aynı
   * döküm iki ayrı isimle sunulurdu ve biri gerekçe isterken öteki
   * istemezdi — kullanıcının "hangisi doğru" diye sormasına yol açan
   * türden bir tutarsızlık.
   */
  const digerleri = (personel.data?.personel ?? []).filter((k) => k.id !== benimKimligim);

  return (
    <section className="rounded-kart border-notr-200 bg-notr-0 shadow-kart flex flex-col gap-3 border p-4">
      <h3 className="text-notr-900 text-sm font-semibold">Kişi bazlı döküm</h3>
      <p className="text-notr-600 text-xs">
        Kendi dökümünüzü gerekçesiz açabilirsiniz.
        {/*
          İkinci cümle KOŞULLU. `ALL` kapsamı olmayan kişiye
          "başkasının dökümü yazılı gerekçe ister" demek, gerekçe
          yazınca açılacağını söylemektir — açılmaz. Yetkisi olmayana
          yetkinin şartını anlatmak, şartı sağlarsa olacağını vaat
          etmek olur.
        */}
        {baskasiniGorebilir === true &&
          " Başkasının dökümü yazılı gerekçe ister ve açılışın kendisi kayda geçer."}
      </p>

      {/*
        Kişi seçimi YALNIZCA başkasını görebilene çiziliyor. Tek
        seçeneklik bir açılır liste, seçim varmış gibi görünüp
        olmayan bir şey vaat ediyordu.
      */}
      {baskasiniGorebilir === true && (
        <Secim
          etiket="Kim"
          value={kim}
          onChange={(e) => {
            setKim(e.target.value);
            setIstenen(null);
          }}
        >
          <option value={BEN}>Kendi dökümüm</option>
          {digerleri.map((p) => (
            <option key={p.id} value={p.id}>
              {p.firstName} {p.lastName}
            </option>
          ))}
        </Secim>
      )}

      {baskasi && (
        <MetinAlani
          etiket="Gerekçe"
          rows={2}
          value={gerekce}
          onChange={(e) => setGerekce(e.target.value)}
          yardim="En az 15 karakter. Bu metin denetim kaydına yazılır ve silinemez."
        />
      )}

      <div>
        <Buton
          disabled={gerekceKisa || kimlikYok}
          onClick={() => {
            if (secilenKimlik === null) return;
            setIstenen({ kullanici: secilenKimlik, gerekce: baskasi ? gerekce.trim() : "" });
          }}
        >
          Dökümü getir
        </Buton>
      </div>

      {/*
        Düğme sebepsiz sönük durmuyor. Kimliği okuyamadığımız hâl
        gerçek hayatta neredeyse görünmez (kabuk aynı yanıtı zaten
        almış oluyor) ama görünürse kullanıcı ne yapacağını bilmeli.
      */}
      {kimlikYok && !ben.isPending && (
        <p className="text-notr-600 text-sm">
          Kimliğiniz okunamadığı için döküm istenemiyor. Sayfayı yenileyin.
        </p>
      )}

      {dokum.isFetching && <Iskelet satir={3} />}

      {dokum.isError && (
        <p role="alert" className="text-hata-700 text-sm font-medium">
          {dokum.error instanceof ApiHatasi ? dokum.error.message : "Döküm alınamadı."}
        </p>
      )}

      {dokum.data && !dokum.isFetching && (
        <div className="flex flex-col gap-3">
          <p className="text-notr-700 text-sm">
            {new Date(dokum.data.baslangic).toLocaleDateString("tr-TR")} –{" "}
            {new Date(dokum.data.bitis).toLocaleDateString("tr-TR")} arasında{" "}
            <strong>{dokum.data.toplamIslem}</strong> işlem.
          </p>

          {dokum.data.moduller.length === 0 ? (
            <p className="text-notr-600 text-sm">Bu aralıkta kayıt yok.</p>
          ) : (
            <ul className="flex flex-col gap-1">
              {dokum.data.moduller.map((m) => (
                <li key={m.anahtar} className="flex justify-between text-sm">
                  <span className="text-notr-700">{modulAdi(m.anahtar)}</span>
                  <span className="text-notr-900 font-medium tabular-nums">{m.islemSayisi}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
